import React, { useState } from 'react';

import PropTypes from 'prop-types';

import { Button, Col, FormGroup, Label, Row } from 'reactstrap';

import { connect } from 'react-redux';

import { change, Field, Form, formValueSelector, reduxForm } from 'redux-form';

import { toastr } from 'react-redux-toastr';

import FormItem from '../../../common/forms/FormItem';

import TextInput from '../../../common/forms/input/TextInput';

import BusinessSelect from '../../../common/forms/select/BusinessSelect';

import CustomerSelect from '../../../common/forms/select/CustomerSelect';

import DynamicForm from '../../../common/forms/DynamicForm';

import FormFooter from '../../../common/forms/FormFooter';

import { getManualVoucherBySeriesAndDocumentCode } from '../../../../actions/accounting/ManualVoucher';

import { verifyDeposit } from '../../../../actions';

import { generateBankAccountColumns } from '../../../../config/dynamicFormFields';

import { tzNormalizeDate } from '../../../../utils/date';

const FORM_NAME = 'ManualVoucherPaymentForm';

export const ManualVoucherPaymentForm = ({
  handleSubmit,
  dispatchChange,
  dispatchGetManualVoucherBySeriesAndDocumentCode,
  documentSeries,
  documentCode,
  manualVoucherId,
  isNaturalPerson,
  loading,
  payments,
}) => {
  const [manualVoucher, setManualVoucher] = useState(null);

  const [verifyingIndex, setVerifyingIndex] = useState(null);

  const clearManualVoucher = () => {
    setManualVoucher(null);

    dispatchChange(FORM_NAME, 'manualVoucherId', null);
  };

  const clearVoucherSearchFields = () => {
    dispatchChange(FORM_NAME, 'documentSeries', null);

    dispatchChange(FORM_NAME, 'documentCode', null);
  };

  const handleBusinessChange = (event, business) => {
    if (!business) {
      return;
    }

    clearManualVoucher();

    dispatchChange(FORM_NAME, 'customer', null);

    clearVoucherSearchFields();
  };

  const handleCustomerChange = (event, customer) => {
    if (!customer) {
      return;
    }

    clearManualVoucher();

    dispatchChange(FORM_NAME, 'business', null);

    clearVoucherSearchFields();
  };

  const handleNaturalPersonChange = (event, value) => {
    clearManualVoucher();

    dispatchChange(FORM_NAME, 'business', null);

    dispatchChange(FORM_NAME, 'customer', null);

    clearVoucherSearchFields();

    dispatchChange(FORM_NAME, 'isNaturalPerson', value);
  };

  const handleVoucherSearchFieldChange = () => {
    if (manualVoucherId) {
      clearManualVoucher();
    }
  };

  const searchManualVoucher = async () => {
    if (!documentSeries || !documentCode) {
      return;
    }

    const response = await dispatchGetManualVoucherBySeriesAndDocumentCode({
      documentSeries,
      documentCode,
    });

    if (!response) {
      clearManualVoucher();

      return;
    }

    setManualVoucher(response);

    dispatchChange(FORM_NAME, 'manualVoucherId', response.id);

    dispatchChange(FORM_NAME, 'business', null);

    dispatchChange(FORM_NAME, 'customer', null);
  };

  const clearVoucherSearch = () => {
    clearManualVoucher();

    clearVoucherSearchFields();
  };

  const onChangeVerifiableField = (index) => {
    dispatchChange(FORM_NAME, `payments[${index}].verified`, false);
  };

  const handleVerifyDeposit = async (index) => {
    const row = payments && payments[index];

    if (
      !row ||
      !row.bankAccountId ||
      !row.operationNumber ||
      !row.depositDate
    ) {
      toastr.warning(
        'Atención',
        'Complete cuenta bancaria, número de operación y fecha antes de verificar',
      );
      return;
    }

    setVerifyingIndex(index);

    try {
      const result = await verifyDeposit({
        bankAccountId: row.bankAccountId.value,
        transactionId: row.operationNumber,
        operationDate: tzNormalizeDate({
          date: row.depositDate,
          format: 'DD-MM-YYYY',
        }),
      });

      if (result) {
        if (result.operationDate) {
          dispatchChange(
            FORM_NAME,
            `payments[${index}].depositDate`,
            tzNormalizeDate({ date: result.operationDate }),
          );
        }
        dispatchChange(FORM_NAME, `payments[${index}].verified`, true);
        dispatchChange(
          FORM_NAME,
          `payments[${index}].actualAmountDeposited`,
          result.amount,
        );
        toastr.success(
          'Depósito verificado',
          `Contrato: ${result.contractNumber} - Monto: ${result.amount} - N° Orden: ${result.transactionId}`,
        );
      }
    } finally {
      setVerifyingIndex(null);
    }
  };

  const bankAccountColumns = generateBankAccountColumns({
    onClickVerify: handleVerifyDeposit,
    onChangeVerifiableField,
    verifyingIndex,
    listName: 'payments',
  });

  return (
    <Form onSubmit={handleSubmit}>
      <h4>Información del Pago</h4>

      {!manualVoucherId && (
        <>
          <FormGroup>
            <Label>
              <Field
                name="isNaturalPerson"
                component="input"
                type="checkbox"
                onChange={handleNaturalPersonChange}
              />{' '}
              Es persona natural
            </Label>
          </FormGroup>

          {!isNaturalPerson && (
            <FormGroup row>
              <FormItem label="Empresa">
                <Field
                  name="business"
                  component={BusinessSelect}
                  isClearable
                  onChange={handleBusinessChange}
                />
              </FormItem>
            </FormGroup>
          )}

          {isNaturalPerson && (
            <FormGroup row>
              <FormItem label="Persona Natural">
                <Field
                  name="customer"
                  component={CustomerSelect}
                  isClearable
                  onChange={handleCustomerChange}
                />
              </FormItem>
            </FormGroup>
          )}
        </>
      )}

      <hr />

      <h4>Comprobante Manual</h4>

      <p className="text-muted">
        Puede buscar un comprobante manual por serie y correlativo. Si encuentra
        uno, no será necesario seleccionar una empresa o persona natural.
      </p>

      {!manualVoucherId && (
        <Row>
          <Col lg={4} md={4} sm={12}>
            <FormGroup>
              <FormItem label="Serie">
                <Field
                  name="documentSeries"
                  component={TextInput}
                  placeholder="Ej. F919"
                  onChange={handleVoucherSearchFieldChange}
                />
              </FormItem>
            </FormGroup>
          </Col>

          <Col lg={4} md={4} sm={12}>
            <FormGroup>
              <FormItem label="Correlativo">
                <Field
                  name="documentCode"
                  component={TextInput}
                  placeholder="Ej. 2139"
                  onChange={handleVoucherSearchFieldChange}
                />
              </FormItem>
            </FormGroup>
          </Col>

          <Col lg={4} md={4} sm={12} className="d-flex align-items-center">
            <Button
              type="button"
              color="primary"
              outline
              disabled={!documentSeries || !documentCode}
              onClick={searchManualVoucher}
            >
              <i className="fa fa-search" /> Buscar comprobante
            </Button>
          </Col>
        </Row>
      )}

      <Field name="manualVoucherId" component="input" type="hidden" />

      {manualVoucher && (
        <div className="alert alert-success">
          <Row>
            <Col md={10}>
              <h5>Comprobante encontrado</h5>

              <div>
                <strong>Comprobante: </strong>
                {manualVoucher.documentSeries}-{manualVoucher.documentCode}
              </div>

              {manualVoucher.voucherType && (
                <div>
                  <strong>Tipo: </strong>
                  {manualVoucher.voucherType.name}
                </div>
              )}

              {manualVoucher.business && (
                <>
                  <div>
                    <strong>Empresa: </strong>
                    {manualVoucher.business.name}
                  </div>

                  <div>
                    <strong>RUC: </strong>
                    {manualVoucher.business.businessTaxId}
                  </div>
                </>
              )}

              {manualVoucher.customer && (
                <>
                  <div>
                    <strong>Persona Natural: </strong>
                    {manualVoucher.customer.fullName}
                  </div>

                  {manualVoucher.customer.idDocumentNumber && (
                    <div>
                      <strong>Documento: </strong>
                      {manualVoucher.customer.idDocumentNumber}
                    </div>
                  )}
                </>
              )}

              <div>
                <strong>Monto Total: </strong>
                {manualVoucher.totalAmount} {manualVoucher.currencyCode}
              </div>
            </Col>

            <Col md={2} className="text-right">
              <Button
                type="button"
                color="danger"
                outline
                size="sm"
                onClick={clearVoucherSearch}
              >
                <i className="fa fa-times" /> Quitar
              </Button>
            </Col>
          </Row>
        </div>
      )}

      <hr />

      <h4>Depósitos</h4>

      <DynamicForm
        name="payments"
        columns={bankAccountColumns}
        title="Pago"
        showAddButton
        showRemoveButton
        initialValues={[{}]}
        labelWithoutElements="No hay pagos registrados"
      />

      <FormFooter saveButtonDisabled={loading} />
    </Form>
  );
};

ManualVoucherPaymentForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,

  dispatchChange: PropTypes.func.isRequired,

  dispatchGetManualVoucherBySeriesAndDocumentCode: PropTypes.func.isRequired,

  documentSeries: PropTypes.string,

  documentCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  manualVoucherId: PropTypes.number,

  isNaturalPerson: PropTypes.bool,

  loading: PropTypes.bool,

  payments: PropTypes.arrayOf(
    PropTypes.shape({
      bankAccountId: PropTypes.shape({
        value: PropTypes.number,
        bankId: PropTypes.number,
      }),
      operationNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      depositDate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      verified: PropTypes.bool,
    }),
  ),
};

ManualVoucherPaymentForm.defaultProps = {
  documentSeries: null,
  documentCode: null,
  manualVoucherId: null,
  isNaturalPerson: false,
  loading: false,
  payments: [],
};

const validate = (values) => {
  const errors = {};

  if (!values.manualVoucherId) {
    if (values.isNaturalPerson) {
      if (!values.customer) {
        errors.customer =
          'Debe seleccionar una persona natural o buscar un comprobante manual';
      }
    } else if (!values.business) {
      errors.business =
        'Debe seleccionar una empresa o buscar un comprobante manual';
    }
  }

  if (!values.payments || values.payments.length === 0) {
    errors.payments = 'Debe registrar al menos un pago';
  }

  return errors;
};

const selector = formValueSelector(FORM_NAME);

const mapStateToProps = (state) => ({
  documentSeries: selector(state, 'documentSeries'),

  documentCode: selector(state, 'documentCode'),

  manualVoucherId: selector(state, 'manualVoucherId'),

  isNaturalPerson: selector(state, 'isNaturalPerson'),

  payments: selector(state, 'payments') || [],

  loading: !state.CorporateUnit.ManualVoucherPayment.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

const mapDispatchToProps = {
  dispatchChange: change,

  dispatchGetManualVoucherBySeriesAndDocumentCode:
    getManualVoucherBySeriesAndDocumentCode,
};

const formComponent = reduxForm({
  form: FORM_NAME,

  validate,

  initialValues: {
    manualVoucherId: null,
    isNaturalPerson: false,
    business: null,
    customer: null,
    payments: [{}],
  },
})(ManualVoucherPaymentForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
